/**
 * A pipeline's cron in words and its next run, in the viewer's local time.
 *
 * Celery beat reads `schedule_cron` on the wall clock of the pipeline's
 * `schedule_timezone` (UTC for new schedules, Europe/Moscow for a few early
 * ones), while run times reach the console as instants shown in local time —
 * so a UTC "0 7 * * *" sits next to runs at 11:00 for a viewer at UTC+4.
 * Everything here converts through real Date instants instead of shifting
 * hour numbers by hand.
 *
 * Supported: a fixed minute; hour, day of month and day of week each `*`, a
 * number or `* /N` (no lists or ranges); month `*`. Anything else falls back
 * to "по своему расписанию" and no next run.
 */
import { clock, plural, whenInline } from 'utils/ru';
import { t } from 'i18n';

const WEEKDAY_ACC = () => ([t('data.onSunday'), t('data.onMonday'), t('data.onTuesday'), t('data.onWednesday'), t('data.onThursday'), t('data.onFriday'), t('data.onSaturday')]);

function parseField(raw, max) {
  if (raw === '*') return { any: true };
  if (/^\*\/\d+$/.test(raw)) {
    const step = Number(raw.slice(2));
    return step > 0 && step <= max ? { step } : null;
  }
  if (/^\d+$/.test(raw)) {
    const value = Number(raw);
    return value <= max ? { value } : null;
  }
  return null;
}

/** The cron's fields, or null when this module cannot read it. */
export function parseCron(cron) {
  const parts = (cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minute, hour, dom, month, dow] = parts;
  if (!/^\d+$/.test(minute) || Number(minute) > 59 || month !== '*') return null;
  const h = parseField(hour, 23);
  const d = parseField(dom, 31);
  const w = parseField(dow, 7);
  if (!h || !d || !w) return null;
  // cron ORs a restricted day-of-month with a restricted day-of-week; not worth it here.
  if (!d.any && !w.any) return null;
  if (w.step || d.step) return null;
  return { minute: Number(minute), hour: h, dom: d, dow: w.any ? w : { value: w.value % 7 } };
}

const fits = (field, value) =>
  field.any || (field.step ? value % field.step === 0 : field.value === value);

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formats = {};

/** The wall clock of `date` in `zone`; an unknown zone reads as UTC. */
function wallClock(date, zone) {
  if (zone && zone !== 'UTC') {
    try {
      formats[zone] ||= new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hourCycle: 'h23',
        weekday: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
      const p = Object.fromEntries(formats[zone].formatToParts(date).map((x) => [x.type, x.value]));
      return { minute: Number(p.minute), hour: Number(p.hour) % 24, day: Number(p.day), weekday: WEEKDAY_SHORT.indexOf(p.weekday) };
    } catch (e) {
      // RangeError: not a zone this browser knows.
    }
  }
  return { minute: date.getUTCMinutes(), hour: date.getUTCHours(), day: date.getUTCDate(), weekday: date.getUTCDay() };
}

const HOUR = 3600 * 1000;

/**
 * The first run strictly after `now`, or null: hour by hour, each hour moved
 * to the cron's minute on the schedule's own wall clock.
 */
export function nextRun(cron, now = new Date(), zone = 'UTC') {
  const c = parseCron(cron);
  if (!c) return null;
  const start = Math.floor(now.getTime() / HOUR) * HOUR;
  for (let i = 0; i <= 24 * 62; i += 1) {
    const base = new Date(start + i * HOUR);
    const at = new Date(base.getTime() + (c.minute - wallClock(base, zone).minute) * 60 * 1000);
    if (at > now) {
      const w = wallClock(at, zone);
      if (fits(c.hour, w.hour) && fits(c.dom, w.day) && fits(c.dow, w.weekday)) return at;
    }
  }
  return null;
}

/**
 * "каждый час" or "каждый час в :15" — the minute in local time too, read
 * off a real run: a UTC "15 * * * *" runs at :45 for a viewer at UTC+05:30.
 */
function hourlyText(cron, now, zone) {
  const at = nextRun(cron, now, zone);
  const minute = at ? at.getMinutes() : 0;
  return minute === 0
    ? t('data.everyHour2')
    : t('data.everyHourAtValue', { value1: String(minute).padStart(2, '0') });
}

/** "каждый день в 11:00", "каждые 3 часа", "раз в неделю, в понедельник в 10:00". */
export function scheduleText(cron, now = new Date(), zone = 'UTC') {
  if (!cron) return t('data.manual');
  const c = parseCron(cron);
  if (!c) return t('data.onACustomSchedule');
  if (c.hour.any && c.dom.any && c.dow.any) return hourlyText(cron, now, zone);
  if (c.hour.step && c.dom.any && c.dow.any) {
    const n = c.hour.step;
    return n === 1 ? hourlyText(cron, now, zone) : t('data.everyValueValue', { value1: n, value2: plural(n, [t('data.hour'), t('data.hours'), t('data.hours2')]) });
  }
  if (c.hour.value === undefined) return t('data.onACustomSchedule');
  // A fixed time: render one real occurrence in local time, so the clock and
  // (for weekly / monthly) the day are both converted.
  const at = nextRun(cron, now, zone);
  if (!at) return t('data.onACustomSchedule');
  if (c.dom.any && c.dow.any) return t('data.everyDayAtValue', { value1: clock(at) });
  if (!c.dow.any) return t('data.weeklyValueAtValue', { value1: WEEKDAY_ACC()[at.getDay()], value2: clock(at) });
  return t('data.monthlyOnDayValueAtValue', { value1: at.getDate(), value2: clock(at) });
}

/** "завтра в 11:00", or null when the schedule is unreadable or manual. */
export function nextRunText(cron, now = new Date(), zone = 'UTC') {
  const at = cron ? nextRun(cron, now, zone) : null;
  return at ? whenInline(at.toISOString(), now) : null;
}
